import { useState } from "react";
import { ExternalLink, Link2, Plus, Trash2, Pencil, Gift, ShoppingBag, Share2, TrendingDown } from "lucide-react";
import { ShareWishlistDialog } from "@/components/shop/ShareWishlistDialog";
import { isRecentDrop } from "@/components/shop/PriceDrops";
import { api, fmtMoney, safeUrl, safeImg } from "@/lib/api";
import { cardCls, CheckCircle } from "@/components/shared";
import { SortableGroup, useSortableRow, DragHandle } from "@/components/Sortable";
import { useDroppable } from "@dnd-kit/core";

const ItemThumb = ({ src, id }) => {
  const [broken, setBroken] = useState(false);
  if (broken) return null;
  return <img src={safeImg(src)} alt="" data-testid={`shop-item-image-${id}`} onError={() => setBroken(true)} className="h-12 w-12 shrink-0 rounded-lg object-cover border border-t-line bg-t-surface" />;
};

const ItemRow = ({ item, list, todoName, onChange, onEdit }) => {
  const s = useSortableRow(item.id);
  const base = `/shop-lists/${list.id}/items/${item.id}`;
  const toggle = async () => onChange((await api.patch(base, { bought: !item.bought })).data);
  const remove = async () => onChange((await api.delete(base)).data);
  return (
    <li ref={s.ref} style={s.style} data-testid={`shop-item-${item.id}`}
      className={`group flex items-start gap-2.5 py-3 border-b border-t-surface last:border-0 ${s.isDragging ? "opacity-40" : ""}`}>
      <DragHandle handle={s.handle} testId={`shop-item-drag-${item.id}`} />
      <CheckCircle checked={item.bought} onClick={toggle} color="rgb(var(--t-c2))" testId={`shop-item-check-${item.id}`} />
      {item.image_url && <ItemThumb src={item.image_url} id={item.id} />}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className={`text-sm font-medium truncate ${item.bought ? "line-through text-t-faint" : ""}`}>{item.name}</p>
          {safeUrl(item.url) && (
            <a href={safeUrl(item.url)} target="_blank" rel="noreferrer" data-testid={`shop-item-link-${item.id}`} className="text-t-primary hover:text-t-primary-hover">
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </div>
        {item.notes && <p className="text-xs text-t-muted mt-0.5">{item.notes}</p>}
        {todoName && (
          <span data-testid={`shop-item-linked-badge-${item.id}`} className="inline-flex items-center gap-1 mt-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium border bg-t-c2-soft text-t-c2-ink border-t-c2-line">
            <Link2 className="h-3 w-3" /> {todoName}
          </span>
        )}
      </div>
      {item.price != null && (
        <span className="text-right shrink-0">
          <span className="block text-sm font-medium">{fmtMoney(item.price)}</span>
          {isRecentDrop(item) && (
            <span data-testid={`shop-item-price-drop-${item.id}`} className="inline-flex items-center gap-1 mt-0.5 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-t-c2-soft text-t-c2-ink border border-t-c2-line">
              <TrendingDown className="h-3 w-3" /> was {fmtMoney(item.previous_price)}
            </span>
          )}
        </span>
      )}
      <div className="flex opacity-0 group-hover:opacity-100 transition-opacity">
        <button data-testid={`edit-shop-item-${item.id}`} onClick={onEdit} className="p-1.5 rounded-lg hover:bg-t-surface"><Pencil className="h-3.5 w-3.5" /></button>
        <button data-testid={`delete-shop-item-${item.id}`} onClick={remove} className="p-1.5 rounded-lg hover:bg-t-primary-soft text-t-danger"><Trash2 className="h-3.5 w-3.5" /></button>
      </div>
    </li>
  );
};

export const ShopListCard = ({ list, todos, onChange, onDelete, onAdd, onEdit, highlight }) => {
  const Icon = list.kind === "wishlist" ? Gift : ShoppingBag;
  const total = list.items.reduce((s, i) => s + (i.price || 0), 0);
  const nameOf = (id) => todos.find((t) => t.id === id)?.name;
  const { setNodeRef } = useDroppable({ id: `list:${list.id}` });
  const [sharing, setSharing] = useState(false);
  return (
    <div ref={setNodeRef} className={`${cardCls} p-6 flex flex-col fade-up hover:-translate-y-0.5 ${highlight ? "ring-2 ring-t-primary/60 bg-t-primary-soft/40" : ""}`} data-testid={`shop-list-${list.id}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${list.kind === "wishlist" ? "bg-t-c5-soft text-t-c5" : "bg-t-c2-soft text-t-c2"}`}>
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-serif-display text-2xl font-semibold leading-tight">{list.name}</h3>
            <p className="text-xs text-t-muted">{list.items.length} items · {fmtMoney(total)}</p>
          </div>
        </div>
        <div className="flex">
        {list.kind === "wishlist" && (
          <button data-testid={`share-shop-list-${list.id}`} onClick={() => setSharing(true)} title="Share with family"
            className={`p-2 rounded-lg hover:bg-t-surface ${list.share_token ? "text-t-primary" : "text-t-muted"}`}><Share2 className="h-4 w-4" /></button>
        )}
        <button data-testid={`delete-shop-list-${list.id}`} onClick={onDelete} className="p-2 rounded-lg hover:bg-t-primary-soft text-t-danger"><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
      <ShareWishlistDialog open={sharing} onOpenChange={setSharing} list={list} onChange={onChange} />
      <SortableGroup ids={list.items.map((i) => i.id)}>
        <ul className="flex-1 min-h-[24px]">
          {list.items.map((it) => (
            <ItemRow key={it.id} item={it} list={list} todoName={nameOf(it.linked_todo_list_id)} onChange={onChange} onEdit={() => onEdit(it)} />
          ))}
        </ul>
      </SortableGroup>
      {highlight && <p className="text-xs text-center text-t-primary font-medium mt-2" data-testid={`shop-list-drop-hint-${list.id}`}>Drop to move here</p>}
      <button data-testid={`add-shop-item-${list.id}`} onClick={onAdd}
        className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-dashed border-t-line-strong text-sm text-t-c4 hover:bg-t-surface transition-colors">
        <Plus className="h-4 w-4" /> Add item
      </button>
    </div>
  );
};
